import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Button,
  Box,
  Alert,
  SpaceBetween,
  Textarea,
  Spinner,
  Table,
  ExpandableSection,
  StatusIndicator,
  Toggle,
} from '@cloudscape-design/components';
import { ResponseCard } from './ResponseCard';
import { apiService } from '../services/ApiService';
import { useStreamingResponses } from '../hooks/useStreamingResponses';
import { useFallbackPolling } from '../hooks/useFallbackPolling';

interface TestScenarioProps {
  testId: number;
  testName: string;
  testPrompt: string;
  testDescription: string;
  userId: string;
  webSearchDefault: boolean;
}

export const TestScenario: React.FC<TestScenarioProps> = ({
  testId,
  testName,
  testPrompt,
  testDescription,
  userId,
  webSearchDefault,
}) => {
  const [demoRunId, setDemoRunId] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRatingInProgress, setIsRatingInProgress] = useState(false);
  const [editablePrompt, setEditablePrompt] = useState(testPrompt);
  const [webSearchEnabled, setWebSearchEnabled] = useState(webSearchDefault);
  const [round2Ratings, setRound2Ratings] = useState<Record<string, Record<string, { rating: number; rationale: string }>> | null>(null);
  const [expectedModelIds, setExpectedModelIds] = useState<string[]>([]);

  // Use streaming responses hook (replaces polling + WebSocket inline logic)
  const { responses, allComplete, queuePosition, connectionState } = useStreamingResponses({
    demoRunId,
    isRunning,
    expectedModelIds,
  });

  // Use fallback polling hook (activates only when WebSocket is in fallback state)
  const { fallbackResponses } = useFallbackPolling({
    demoRunId,
    isRunning,
    connectionState,
    currentResponses: responses,
  });

  // Compute display responses: use fallback data when available, otherwise streaming data
  const displayResponses = fallbackResponses ?? responses;

  // Track whether a new run was just started to avoid the allComplete race
  const justStartedRef = React.useRef(false);

  // When allComplete becomes true, stop running
  useEffect(() => {
    if (allComplete && isRunning && !justStartedRef.current) {
      setIsRunning(false);
    }
    // Clear the flag after the first render where demoRunId is set
    if (justStartedRef.current && demoRunId) {
      justStartedRef.current = false;
    }
  }, [allComplete, isRunning, demoRunId]);

  const handleRunTest = async () => {
    try {
      setError(null);
      justStartedRef.current = true;
      setDemoRunId(null);  // Reset to clear previous responses and allComplete
      setExpectedModelIds([]);
      setRound2Ratings(null);
      setIsRunning(true);

      // Create demo run — send custom prompt only if user modified it
      const customPrompt = editablePrompt !== testPrompt ? editablePrompt : undefined;
      const result = await apiService.createDemoRun(testId, userId, customPrompt, webSearchEnabled);
      setExpectedModelIds(result.modelIds || []);
      setDemoRunId(result.demoRunId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start demo run');
      setIsRunning(false);
    }
  };

  const handleRateYourself = async () => {
    if (!demoRunId) return;

    try {
      setIsRatingInProgress(true);
      setError(null);
      setRound2Ratings(null);
      await apiService.triggerRound2Rating(demoRunId);
      
      // Poll for results every 3 seconds, up to 3 minutes
      const maxAttempts = 60;
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const details = await apiService.getDemoRunDetail(demoRunId);
          if (details.round2Ratings && Object.keys(details.round2Ratings).length > 0) {
            setRound2Ratings(details.round2Ratings);
            setIsRatingInProgress(false);
            clearInterval(poll);
          } else if (attempts >= maxAttempts) {
            setIsRatingInProgress(false);
            setError('Rating timed out. Try again later.');
            clearInterval(poll);
          }
        } catch {
          // ignore polling errors, keep trying
        }
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger Round 2 rating');
      setIsRatingInProgress(false);
    }
  };

  const sortedResponses = Object.values(displayResponses).sort((a, b) => {
    if (a.isGrok && !b.isGrok) return -1;
    if (!a.isGrok && b.isGrok) return 1;
    return a.modelName.localeCompare(b.modelName);
  });

  const renderConnectionStatus = () => {
    if (connectionState === 'connected' || !isRunning) return null;

    switch (connectionState) {
      case 'reconnecting':
        return <StatusIndicator type="warning">Reconnecting...</StatusIndicator>;
      case 'fallback':
        return <StatusIndicator type="warning">Real-time streaming unavailable</StatusIndicator>;
      case 'disconnected':
        return <StatusIndicator type="stopped">Disconnected</StatusIndicator>;
      default:
        return null;
    }
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description={testDescription}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {renderConnectionStatus()}
                <Button
                  variant="primary"
                  onClick={handleRunTest}
                  disabled={isRunning}
                  loading={isRunning}
                >
                  Run Test
                </Button>
                {allComplete && (
                  <Button
                    onClick={handleRateYourself}
                    disabled={isRatingInProgress}
                    loading={isRatingInProgress}
                  >
                    Rate Yourself
                  </Button>
                )}
              </SpaceBetween>
            }
          >
            {testName}
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Box variant="p" fontWeight="bold">
            Prompt:
          </Box>
          <Textarea
            value={editablePrompt}
            onChange={({ detail }) => setEditablePrompt(detail.value)}
            placeholder="Enter your prompt..."
            rows={3}
            disabled={isRunning}
          />
          <Toggle
            onChange={({ detail }) => setWebSearchEnabled(detail.checked)}
            checked={webSearchEnabled}
            disabled={isRunning}
          >
            Web search
          </Toggle>
        </SpaceBetween>

        {queuePosition !== null && queuePosition > 0 && (
          <Alert type="info">
            {queuePosition === 1
              ? 'Your demo run is starting...'
              : `Queue position: ${queuePosition}. Your demo run will start soon.`}
          </Alert>
        )}

        {error && <Alert type="error">{error}</Alert>}
      </Container>

      {sortedResponses.length > 0 && (
        <div style={{ 
          display: 'flex', 
          overflowX: 'auto', 
          gap: '16px',
          padding: '8px 0'
        }}>
          {sortedResponses.map(response => (
            <div key={response.modelId} style={{ 
              minWidth: '400px', 
              maxWidth: '400px',
              flexShrink: 0
            }}>
              <ResponseCard response={response} />
            </div>
          ))}
        </div>
      )}
      {isRatingInProgress && (
        <Container>
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>
              Models are rating each other... This may take a minute.
            </Box>
          </Box>
        </Container>
      )}

      {round2Ratings && Object.keys(round2Ratings).length > 0 && (
        <Container header={<Header variant="h2">Round 2: Model Self-Ratings</Header>}>
          <SpaceBetween size="m">
            {Object.entries(round2Ratings).map(([raterModelId, ratedModels]) => (
              <ExpandableSection
                key={raterModelId}
                headerText={`${raterModelId} rated:`}
                defaultExpanded
              >
                <Table
                  columnDefinitions={[
                    { id: 'model', header: 'Rated Model', cell: (item: any) => item.model },
                    { id: 'rating', header: 'Score', cell: (item: any) => `${item.rating}/10` },
                    { id: 'rationale', header: 'Rationale', cell: (item: any) => item.rationale },
                  ]}
                  items={Object.entries(ratedModels).map(([modelId, data]) => ({
                    model: modelId,
                    rating: data.rating,
                    rationale: data.rationale || '-',
                  }))}
                  variant="embedded"
                  sortingDisabled
                />
              </ExpandableSection>
            ))}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};
